import useFormatter from "@/hooks/use-formatter.hook.ts";
import { JobViewFields } from "@/pages/recruit/jobs/components/JobViewFields.tsx";
import { useJobDetailsHistory } from "@/queries/recruit/use-jobs.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import { ApiJob, JobStatusItems } from "@/types/recruit/job.types.ts";
import {
  Button,
  DotSeparator,
  ListItem,
  Modal,
  ModalProps,
  Stack,
  TabItem,
  Tabs,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export interface JobViewModalProps extends ModalProps {
  job: ApiJob;
}

export function JobViewModal({ job, ...props }: JobViewModalProps) {
  const { id } = { ...props };
  const { close } = useModals();
  const { t } = useTranslation();

  const { data: jobHistory } = useJobDetailsHistory(job.id);
  const { renderEmployeeName, renderDateAndTime } = useFormatter();

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.jobs.detailsViewed, {
      [analyticProperties.id]: id,
    });
  });

  return (
    <Modal
      {...props}
      title={job.title}
      withCloser
      layout={"stretch"}
      size={"lg"}
      actions={
        <Stack>
          <Button size={"lg"} onClick={() => close(id)}>
            {t("common:close")}
          </Button>
        </Stack>
      }
    >
      <Tabs>
        <TabItem title={t("common:overview")}>
          <Stack>
            <Title
              size={"xs"}
              header={t("common:configuration")}
              paddingTop={true}
              paddingBottom={true}
            />
            <ListItem
              preTitle={t("common:status")}
              title={JobStatusItems().find((x) => x.id == job.status)?.title}
            />
            <JobViewFields job={job} />
          </Stack>
        </TabItem>
        <TabItem title={t("common:history")}>
          <Stack>
            <Title
              header={t("common:history")}
              size={"xs"}
              paddingTop={true}
              paddingBottom={true}
            />
            <Stack>
              {jobHistory?.status_changes.map((item, index) => (
                <ListItem
                  preTitleSlot={
                    <DotSeparator>
                      <Text className={"text-secondary"}>{renderEmployeeName(item.employee)}</Text>
                      <Text className={"text-secondary"}>
                        {renderDateAndTime(item.datetime_created_at)}
                      </Text>
                    </DotSeparator>
                  }
                  title={JobStatusItems().find((x) => x.id == item.status)?.title}
                  avatarUrl={getEmployeeAvatarUrl(item.employee, "md")}
                  forceShowAvatar={true}
                  key={index}
                />
              ))}
              {jobHistory && jobHistory.created_by && (
                <ListItem
                  preTitleSlot={
                    <DotSeparator>
                      <Text className={"text-secondary"}>
                        {renderEmployeeName(jobHistory.created_by)}
                      </Text>
                      <Text className={"text-secondary"}>
                        {renderDateAndTime(job.datetime_created_at)}
                      </Text>
                    </DotSeparator>
                  }
                  title={t("common:created")}
                  avatarUrl={getEmployeeAvatarUrl(jobHistory.created_by, "md")}
                  forceShowAvatar={true}
                />
              )}
            </Stack>
          </Stack>
        </TabItem>
      </Tabs>
    </Modal>
  );
}
